/**
 * Publishing an AI Service's interface to the public web (tasks 847, 850).
 *
 * Report: `project/reports/platform-custom-domains-account-endpoints.md`.
 *
 * Lives in the Interface tab because it is a decision about the interface, made
 * where the interface is built and where its version history already is.
 *
 * **Two switches, deliberately not one.** The public page serves only when BOTH
 * are set, and they answer different questions:
 *
 *   1. `uiExposed` — is there a public page at all? Owned by the AI Service.
 *   2. The published version — WHICH saved version that page serves. Owned by
 *      the interface. The head moves every time the Composer saves; the
 *      published version does not, so iterating never changes what visitors
 *      see, and rolling back is publishing an older version again.
 *
 * Keeping them separate is what makes "take the page down without losing what
 * was published" and "prepare the next version while the current one serves"
 * both expressible. The section says so on screen rather than in a tooltip.
 *
 * **Exposure is not authorization.** The page is served under the AI Service's
 * own auth policy — publishing an interface can never open a closed service —
 * which is stated here because it is the question every owner asks next.
 */
import { useMemo } from 'react';
import { ArrowTopRightOnSquareIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import type { Endpoint } from '@/types';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useToast } from '@/contexts/ToastContext';
import { useUpdateEndpoint } from '@/hooks/useEndpoints';
import { usePublishGeneratedUi, useUnpublishGeneratedUi } from '@/hooks/useGeneratedUis';
import { useActiveCustomDomainHostnames } from '@/hooks/useCustomDomains';
import { findParentUi, versionLabel } from '@/lib/generatedUi';
import { endpointUiUrl } from '@/utils/endpointUrl';
import { useServiceInterfaceVersions } from './serviceInterfaceVersions';
import { CreateInterfaceButton } from './CreateInterfaceButton';
import { ACCESS_MODE_LABELS, ACCESS_MODE_SENTENCES, serviceAccessMode } from './serviceAccess';

interface ServiceInterfacePublishingSectionProps {
  endpoint: Endpoint;
}

export function ServiceInterfacePublishingSection({
  endpoint,
}: ServiceInterfacePublishingSectionProps) {
  const featureEnabled = useFlag('innovate.fe.service-exposure.manage');
  const toast = useToast();
  const { uis, versions, isLoading } = useServiceInterfaceVersions(endpoint.id);
  const updateEndpoint = useUpdateEndpoint();
  const publishUi = usePublishGeneratedUi(endpoint.id);
  const unpublishUi = useUnpublishGeneratedUi(endpoint.id);
  // The hostnames public URLs are shown in, when the account has any serving.
  const customDomains = useActiveCustomDomainHostnames({ enabled: featureEnabled });
  const customDomain = customDomains[0] ?? null;

  const exposed = endpoint.uiExposed ?? false;
  // The service's actual access policy, so the "exposure is not authorization"
  // line can name it instead of gesturing at it (task 973).
  const accessMode = serviceAccessMode(endpoint);

  /**
   * The parent UI whose version is (or would be) published. The endpoint's
   * `publishedUiId` is authoritative once something has been published; before
   * that, the AI Service's only saved interface is the one a publish would pin.
   */
  const publishedUi = useMemo(() => {
    if (endpoint.publishedUiId) {
      const named = uis.find((ui) => ui.id === endpoint.publishedUiId);
      if (named) return named;
    }
    return uis[0];
  }, [uis, endpoint.publishedUiId]);

  const publishedVersionId = publishedUi?.publishedVersionId ?? null;
  const publishedVersion = versions.find((version) => version.id === publishedVersionId);

  /** The page serves only when the AI Service exposes it AND a version is pinned. */
  const isLive = exposed && !!endpoint.publishedUiId && !!publishedVersionId;
  const publicUrl = endpointUiUrl(endpoint, customDomain);
  /**
   * The other addresses that reach the same page (task 857), in the convention
   * every connection surface now shares: the custom domain leads, the platform
   * URL follows as the one that never stops working, then any further domains
   * the account holds. Composed through the same `endpointUiUrl` helper, so this
   * section and the Developer Info tab cannot spell the address differently.
   */
  const alternateUrls = customDomain
    ? [
        { label: 'Platform URL (always available)', value: endpointUiUrl(endpoint) },
        ...customDomains.slice(1).map((hostname) => ({
          label: `Also on ${hostname}`,
          value: endpointUiUrl(endpoint, hostname),
        })),
      ]
    : [];

  const handleToggleExposed = (next: boolean) => {
    updateEndpoint.mutate(
      { id: endpoint.id, data: { uiExposed: next } },
      {
        onError: () =>
          toast.error(
            next ? 'Could not publish the interface' : 'Could not take the interface down',
            'The AI Service was not changed.'
          ),
      }
    );
  };

  const handleSelectVersion = (versionId: string) => {
    if (!publishedUi) return;

    if (versionId === '') {
      unpublishUi.mutate(publishedUi.id, {
        onError: () =>
          toast.error('Could not unpublish', 'The published version was not changed.'),
      });
      return;
    }

    publishUi.mutate(
      { id: publishedUi.id, versionId },
      {
        onError: () =>
          toast.error('Could not publish that version', 'The published version was not changed.'),
      }
    );
  };

  if (!featureEnabled) return null;

  return (
    <section
      data-testid="service-interface-publishing"
      data-exposed={exposed ? 'true' : 'false'}
      data-live={isLive ? 'true' : 'false'}
      className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            Public interface page
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Serve a saved version of this interface as a standalone page anyone can open — no sign
            in to this app required. The page still calls this AI Service under its own access
            policy, so publishing an interface never opens a closed service.
          </p>
          {/* WHICH policy, not merely that there is one (task 973). "Publishing
              never opens a closed service" only means something if the reader
              can see whether this one is closed — and can get to where it
              changes. */}
          <p
            className="mt-2 text-sm text-gray-500 dark:text-gray-400"
            data-testid="service-interface-publishing-access"
            data-mode={accessMode}
          >
            <span className="font-medium text-gray-700 dark:text-gray-300">
              Access: {ACCESS_MODE_LABELS[accessMode]}.
            </span>{' '}
            {ACCESS_MODE_SENTENCES[accessMode]}{' '}
            <Link
              to={`/services/${endpoint.id}?tab=config&section=access`}
              data-testid="service-interface-publishing-access-link"
              className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              Change who can call it
            </Link>
          </p>
        </div>
      </div>

      {isLoading ? (
        <p
          className="mt-4 text-sm text-gray-500 dark:text-gray-400"
          data-testid="service-interface-publishing-loading"
        >
          Loading saved versions…
        </p>
      ) : versions.length === 0 ? (
        <div
          className="mt-4 rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center dark:border-gray-600 dark:bg-gray-800/50"
          data-testid="service-interface-publishing-empty"
        >
          <SparklesIcon className="mx-auto h-6 w-6 text-gray-400" />
          <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            No UI versions saved yet
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Generate an interface first — there has to be a saved version before there is anything
            to publish.
          </p>
          <CreateInterfaceButton
            testId="service-interface-publishing-create"
            className="mt-3 inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
          />
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={exposed}
              disabled={updateEndpoint.isPending}
              onChange={(event) => handleToggleExposed(event.target.checked)}
              data-testid="service-ui-exposed-toggle"
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
            />
            <span className="ml-2">
              <span className="block text-sm text-gray-900 dark:text-white">
                Serve this interface publicly
              </span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Turning this off takes the page down immediately. The published version is
                remembered, so turning it back on restores the same page.
              </span>
            </span>
          </label>

          {/* The version picker is only a question worth asking once the page
              exists — before that there is nothing for a pinned version to
              appear on. */}
          {exposed && (
            <div data-testid="service-ui-version-picker">
              <label
                htmlFor="service-ui-published-version"
                className="block text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                Published version
              </label>
              <select
                id="service-ui-published-version"
                value={publishedVersionId ?? ''}
                disabled={publishUi.isPending || unpublishUi.isPending}
                onChange={(event) => handleSelectVersion(event.target.value)}
                data-testid="service-ui-published-version"
                className="mt-1 w-full max-w-sm rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Nothing published</option>
                {[...versions].reverse().map((version) => (
                  <option key={version.id} value={version.id}>
                    v{version.version} ·{' '}
                    {versionLabel(version, findParentUi(uis, version), 'Untitled version')}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Visitors keep seeing this version while you iterate. Roll back by
                choosing an earlier one.
              </p>
            </div>
          )}

          {isLive ? (
            <div data-testid="service-ui-public-url">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Public page
                {customDomain && (
                  <span
                    data-testid="service-ui-custom-domain-badge"
                    className="ml-2 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
                  >
                    On your custom domain
                  </span>
                )}
              </p>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-ui-preview-link"
                className="mt-1 inline-flex max-w-full items-center gap-1.5 break-all font-mono text-sm text-indigo-600 hover:underline dark:text-indigo-400"
              >
                {publicUrl}
                <ArrowTopRightOnSquareIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              </a>
              {alternateUrls.length > 0 && (
                <ul className="mt-1 space-y-1" data-testid="service-ui-alternate-urls">
                  {alternateUrls.map((alternate) => (
                    <li
                      key={alternate.value}
                      data-testid="service-ui-alternate-url"
                      className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
                    >
                      <span className="font-medium">{alternate.label}</span>
                      <a
                        href={alternate.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="min-w-0 break-all font-mono hover:underline"
                      >
                        {alternate.value}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
              {publishedVersion && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Serving v{publishedVersion.version}.
                </p>
              )}
            </div>
          ) : (
            exposed && (
              <p
                className="text-sm text-gray-500 dark:text-gray-400"
                data-testid="service-ui-nothing-published"
              >
                Nothing is published yet, so the public page is not reachable. Choose a version
                above to put it live.
              </p>
            )
          )}
        </div>
      )}
    </section>
  );
}

export default ServiceInterfacePublishingSection;
